import React, { useCallback, useEffect, useState } from "react";
import { ManagedResourceTypeHeader } from "../headers/PageHeaders";
import { Table, Button, Tooltip } from "antd";
import { PlusCircleOutlined } from "@ant-design/icons";
import PropTypes from "prop-types";
import {
  deleteResource,
  getAllResourcesOfResourceType,
  getManagedResourceType,
  transferResourceOwner,
  patchResource,
} from "../../actions/api";
import { getMyRequests } from "../../actions";
import {
  getResourceDetails,
  getResourceDisplayColumns,
} from "../common/ResourcesUtils";
import { useDispatch } from "react-redux";

const ManagedResourcesListAdmin = ({
  managedResourceTypeName,
  identityId,
  loading,
}) => {
  const dispatch = useDispatch();
  const [resources, setResources] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [managedResourceType, setManagedResourceType] = useState(false);
  const [searchCriteria, setSearchCriteria] = useState();
  const [ownerFilter, setOwnerFilter] = useState();

  const fetchManagedResourcesType = useCallback(
    async (managedResourceTypeName) => {
      setIsLoading(true);
      const action = await dispatch(
        getManagedResourceType(managedResourceTypeName),
      );
      if (action && action.payload && action.payload.data)
        setManagedResourceType(action.payload.data);
      setIsLoading(false);
    },
    [dispatch],
  );

  useEffect(() => {
    fetchManagedResourcesType(managedResourceTypeName);
  }, [fetchManagedResourcesType, managedResourceTypeName]);

  const fetchResources = useCallback(
    async (managedResourceType, filter, ownerFilter) => {
      setIsLoading(true);
      const action = await dispatch(
        getAllResourcesOfResourceType(
          managedResourceType.resourceTypeName,
          filter,
        ),
      );
      if (action && action.payload && action.payload.data) {
        if (ownerFilter)
          setResources(
            action.payload.data.filter((resource) =>
              resource.owner?.upn
                ? resource.owner.upn.includes(ownerFilter)
                : false,
            ),
          );
        else setResources(action.payload.data);
      }

      setIsLoading(false);
    },
    [dispatch],
  );

  useEffect(() => {
    if (managedResourceType)
      fetchResources(managedResourceType, searchCriteria, ownerFilter);
  }, [fetchResources, managedResourceType, searchCriteria, ownerFilter]);

  const onSearch = async (value) => {
    if (value && value.length >= 2) {
      setSearchCriteria({
        filter: `displayName:Contains:${value}`,
        sort: "displayName:asc",
      });
    } else {
      setSearchCriteria();
      await fetchResources(managedResourceType, undefined, ownerFilter);
    }
  };

  const onDelete = async (item) => {
    await dispatch(deleteResource(item, managedResourceType.resourceTypeName));
    await fetchResources(managedResourceType, searchCriteria, ownerFilter);
  };

  const onApprove = async (item) => {
    await dispatch(
      transferResourceOwner(
        managedResourceType.resourceTypeName,
        item.id,
        item.ownerId,
      ),
    );
    await fetchResources(managedResourceType, searchCriteria, ownerFilter);
  };

  const onOwnerChange = async (item, owner) => {
    await dispatch(
      transferResourceOwner(
        managedResourceType.resourceTypeName,
        item.id,
        owner.id,
      ),
    );
    await dispatch(getMyRequests());
    await fetchResources(managedResourceType, searchCriteria, ownerFilter);
  };

  const onSelectNewAdminGroup = async (item, group) => {
    const data = [
      {
        value: group?.id,
        path: "/administratorsId",
        op: "replace",
      },
    ];
    await dispatch(patchResource(item.managedResourceType, item.id, data));
    await dispatch(getMyRequests());
    await fetchResources(managedResourceType, searchCriteria, ownerFilter);
  };

  const onSearchOwner = async (value) => {
    if (!value) setOwnerFilter();
    else if (value.length >= 2) {
      setOwnerFilter(value);
      // we filter the results in state,
      // as we cannot use filter on the API without first searching for ownerId
    } else setOwnerFilter();
  };

  const resourceColumns = getResourceDisplayColumns(
    onDelete,
    onApprove,
    onOwnerChange,
    onSelectNewAdminGroup,
    identityId,
    managedResourceType.managementUrlTemplate,
    managedResourceType.accessUrlTemplate,
    true,
    onSearchOwner,
    onSearch,
  );

  const renderActionButtons = (managedResourceType) => {
    return (
      <>
        {managedResourceType.createResourceUrl && (
          <Tooltip title="Create a new Resource of this type">
            <Button
              type="default"
              href={managedResourceType.createResourceUrl}
              target="_blank"
              icon={<PlusCircleOutlined />}
            />
          </Tooltip>
        )}
      </>
    );
  };

  return (
    <>
      <ManagedResourceTypeHeader
        title={managedResourceType.resourceTypeName}
        button={renderActionButtons(managedResourceType)}
        subTitle={managedResourceType.description}
      />

      <Table
        loading={isLoading || loading}
        columns={resourceColumns}
        rowKey={(record) => record.id}
        dataSource={resources}
        expandable={{
          expandedRowRender: (record) => getResourceDetails(record, true),
        }}
        locale={{ emptyText: "No resources found" }}
        pagination={{ hideOnSinglePage: true }}
      />
    </>
  );
};

ManagedResourcesListAdmin.propTypes = {
  managedResourceTypeName: PropTypes.string.isRequired,
  loading: PropTypes.bool,
  identityId: PropTypes.string,
};

export default ManagedResourcesListAdmin;
